/**
 * EscapeTopDestinations — visual destination tiles below the search.
 *
 * Founder ruling 18 Feb 2026 PM-6 / PM-11:
 *   - 10 destination cards (extended from 8) for visual balance and
 *     coverage parity with `/enjoy`'s rail.
 *   - Every card MUST have a working image. No blanks, no monograms.
 *   - St-Tropez specifically fixed (the previous Unsplash photo
 *     `5d54f4ba56a3` was 404'ing).
 *   - Fallback chain · Unsplash → onError SVG monogram (last-resort
 *     branded background so a card never appears empty).
 *
 * Doctrine: every tile click runs a real /api/escape/search · the
 * tile is a search-anchor, not a curated landing page.
 *
 * The 10 destinations match the founder list:
 *   Paris · Dubai · Las Vegas · Barcelona · London · Rome ·
 *   New York · Tokyo · St-Tropez · Singapore.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const T = {
    ink: "#211C15", ink3: "#8A8071",
    line: "#E5DCCB", brass: "#8B6914",
};

// 10 destinations · stable Unsplash CDN URLs (verified 18 Feb 2026).
// Each photo-id is pinned · the image cannot silently change.
// `query` is the exact string passed into /api/escape/search.
const TOP_DESTINATIONS = [
    {
        name: "Paris", slug: "paris", query: "Paris",
        image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80&auto=format&fit=crop",
        sub: "France · Lights & Louvre",
    },
    {
        name: "Dubai", slug: "dubai", query: "Dubai",
        image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80&auto=format&fit=crop",
        sub: "United Arab Emirates",
    },
    {
        name: "Las Vegas", slug: "las-vegas", query: "Las Vegas",
        image: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=800&q=80&auto=format&fit=crop",
        sub: "United States · Nevada",
    },
    {
        name: "Barcelona", slug: "barcelona", query: "Barcelona",
        image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800&q=80&auto=format&fit=crop",
        sub: "Spain · Catalonia",
    },
    {
        name: "London", slug: "london", query: "London",
        image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80&auto=format&fit=crop",
        sub: "United Kingdom",
    },
    {
        name: "Rome", slug: "rome", query: "Rome",
        image: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80&auto=format&fit=crop",
        sub: "Italy · The Eternal City",
    },
    {
        name: "New York", slug: "new-york", query: "New York",
        image: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&q=80&auto=format&fit=crop",
        sub: "United States",
    },
    {
        name: "Tokyo", slug: "tokyo", query: "Tokyo",
        image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80&auto=format&fit=crop",
        sub: "Japan · Old and new",
    },
    {
        name: "St-Tropez", slug: "st-tropez", query: "St Tropez",
        // Founder ruling 18 Feb 2026 PM-11 · previous photo-id
        // `5d54f4ba56a3` was a 404. Replaced with a verified
        // Riviera harbour shot · checked 18 Feb 2026 (HTTP 200).
        // If this URL ever 404s the DestinationCard falls back
        // to a Concierge® brass monogram tile (never blank).
        image: "https://images.unsplash.com/photo-1499678329028-101435549a4e?w=800&q=80&auto=format&fit=crop",
        sub: "France · French Riviera",
    },
    {
        name: "Singapore", slug: "singapore", query: "Singapore",
        image: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=800&q=80&auto=format&fit=crop",
        sub: "Garden city",
    },
];

/** Lookup helper · used by /escape/destination/:slug to find the
 *  matching destination meta for the hero / image / query. */
export function destinationBySlug(slug) {
    return TOP_DESTINATIONS.find((d) => d.slug === slug) || null;
}

/* ── Fallback monogram tile · LAST resort if the photo CDN fails.
 *  Renders a clean Concierge® brass / paper card with the destination
 *  initial. Never blank. */
function MonogramFallback({ name }) {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    return (
        <div
            style={{
                position: "absolute", inset: 0,
                background: `linear-gradient(135deg, #F4EFE6 0%, #EFE6D3 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: 14,
            }}
            aria-hidden="true"
        >
            <span
                className="t-serif"
                style={{
                    fontSize: 64,
                    color: T.brass,
                    fontWeight: 500,
                    letterSpacing: "-0.04em",
                    opacity: 0.7,
                }}
            >{initial}</span>
        </div>
    );
}

function DestinationCard({ dest, onPick }) {
    const [errored, setErrored] = useState(false);
    const navigate = useNavigate();

    function handleClick() {
        // Founder ruling 19 Feb 2026 · destination tiles now route to
        // dedicated `/escape/destination/:slug` pages (Viator-style)
        // rather than inline-mutating the search bar. The legacy
        // `onPick` prop is still honoured for tests + admin panels.
        if (dest.slug) {
            navigate(`/escape/destination/${dest.slug}`);
            return;
        }
        onPick?.(dest.query);
    }

    return (
        <button
            type="button"
            data-testid={`escape-top-destination-${dest.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            onClick={handleClick}
            style={{
                background: "transparent",
                border: 0, padding: 0,
                cursor: "pointer", textAlign: "left",
                fontFamily: "inherit",
            }}
        >
            <div
                style={{
                    width: "100%", aspectRatio: "4/5",
                    borderRadius: 14,
                    border: `1px solid ${T.line}`,
                    position: "relative",
                    overflow: "hidden",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    background: errored
                        ? "transparent"
                        : `linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55)), url(${dest.image}) center/cover no-repeat`,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(33,28,21,0.18)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                }}
            >
                {errored && <MonogramFallback name={dest.name} />}
                {/* Hidden <img> probes the photo CDN so we can react if
                    it 404s without burdening the visible <div>. */}
                <img
                    src={dest.image}
                    alt={`${dest.name} · ${dest.sub}`}
                    onError={() => setErrored(true)}
                    style={{ display: "none" }}
                    data-testid={`escape-top-destination-img-${dest.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                />
                <div style={{
                    position: "absolute", bottom: 14, left: 14, right: 14,
                    color: errored ? T.ink : "#fff",
                }}>
                    <div className="t-serif" style={{ fontSize: 22, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
                        {dest.name}
                    </div>
                    <div style={{ fontSize: 12, color: errored ? T.ink3 : "rgba(255,255,255,0.85)", marginTop: 4 }}>
                        {dest.sub}
                    </div>
                </div>
            </div>
        </button>
    );
}

export default function EscapeTopDestinations({ onPick }) {
    return (
        <section
            data-testid="escape-top-destinations"
            style={{ padding: "8px 22px 56px" }}
        >
            <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                        <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.brass, fontFamily: "ui-monospace, monospace", marginBottom: 6 }}>
                            Top destinations
                        </div>
                        <div className="t-serif" style={{ fontSize: 22, color: T.ink }}>
                            Places we travel often
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 16,
                    }}
                >
                    {TOP_DESTINATIONS.map((d) => (
                        <DestinationCard key={d.name} dest={d} onPick={onPick} />
                    ))}
                </div>
            </div>
        </section>
    );
}

// Exported for tests · founder ruling 18 Feb 2026 PM-11 ·
// `test_escape_top_destinations_has_10_cards`.
export { TOP_DESTINATIONS };
